/**
 * LogViewer
 *
 * Renders strategy log entries (from ctx.log() calls) in a scrollable,
 * filterable, monospace panel.  Designed to be embedded inside the backtest
 * detail view so users can debug their strategies.
 */

import { useState, useMemo } from 'react';

export interface LogEntry {
  timestamp: number;
  message: string;
}

interface LogViewerProps {
  logs: LogEntry[];
}

/** Format a Unix-ms timestamp as "HH:MM:SS UTC" */
function formatTimestamp(ms: number): string {
  const d = new Date(ms);
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  const ss = String(d.getUTCSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss} UTC`;
}

function formatLogLine(entry: LogEntry): string {
  return `[${formatTimestamp(entry.timestamp)}] ${entry.message}`;
}

export function LogViewer({ logs }: LogViewerProps) {
  const [filter, setFilter] = useState('');

  const filteredLogs = useMemo(() => {
    if (!filter.trim()) return logs;
    const lower = filter.toLowerCase();
    return logs.filter((e) => e.message.toLowerCase().includes(lower));
  }, [logs, filter]);

  const handleCopyAll = () => {
    const text = logs.map(formatLogLine).join('\n');
    navigator.clipboard.writeText(text).catch(() => {
      // Silently ignore clipboard errors — not financial logic
    });
  };

  const isFiltered = filter.trim().length > 0;

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-700">
        <span className="text-sm font-medium text-gray-300">
          {isFiltered
            ? `Strategy logs (${filteredLogs.length} / ${logs.length})`
            : `Strategy logs (${logs.length} entries)`}
        </span>
        <button
          onClick={handleCopyAll}
          disabled={logs.length === 0}
          className="px-2 py-1 text-xs rounded bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="Copy all"
        >
          Copy all
        </button>
      </div>

      {/* Filter bar */}
      <div className="px-4 py-2 border-b border-gray-700/60">
        <input
          type="text"
          placeholder="Filter log entries..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs text-gray-200 placeholder-gray-500 focus:outline-none focus:border-primary-500"
        />
      </div>

      {/* Log rows */}
      <div className="overflow-y-auto" style={{ maxHeight: '320px' }}>
        {logs.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-500 text-center">
            No strategy log entries &mdash; call{' '}
            <code className="text-gray-400 font-mono text-xs">ctx.log()</code>{' '}
            from your strategy to debug.
          </p>
        ) : filteredLogs.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-500 text-center">
            No entries match &ldquo;{filter}&rdquo;
          </p>
        ) : (
          <ul className="divide-y divide-gray-700/40">
            {filteredLogs.map((entry, idx) => (
              <li
                key={idx}
                className="px-4 py-1.5 font-mono text-xs text-gray-300 whitespace-pre-wrap hover:bg-gray-700/30"
              >
                <span className="text-gray-500 select-none mr-2">
                  [{formatTimestamp(entry.timestamp)}]
                </span>
                {entry.message}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
